import type { components } from "../../shared/api/generated/trading-engine.js";
import type { AnalysisReport } from "./queries.js";

export type ChartAnnotation = components["schemas"]["AiAnalysisChartAnnotation"];
export interface AnalysisCandle {
  readonly evidenceId: string;
  readonly date: string;
  readonly open: number;
  readonly high: number;
  readonly low: number;
  readonly close: number;
  readonly volume: number;
}

/** Only the report's saved OHLCV evidence may supply chart coordinates. */
export function analysisChartModel(report: AnalysisReport) {
  const snapshot = report.snapshot;
  if (snapshot?.symbol !== report.symbol)
    return {
      candles: [],
      annotations: [],
      error: "This report has no matching saved market data.",
    };
  const candles: AnalysisCandle[] = [];
  for (const item of snapshot.evidence) {
    if (!item.id.startsWith(`${report.symbol}:`)) continue;
    const date = item.id.slice(report.symbol.length + 1);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    const { open, high, low, close, volume } = item.values;
    if (
      !Number.isFinite(Date.parse(date)) ||
      new Date(date).toISOString().slice(0, 10) !== date ||
      date > snapshot.lastSessionDate ||
      open === undefined ||
      high === undefined ||
      low === undefined ||
      close === undefined ||
      volume === undefined ||
      ![open, high, low, close, volume].every(Number.isFinite) ||
      Math.min(open, high, low, close) <= 0 ||
      volume < 0 ||
      low > Math.min(open, close) ||
      high < Math.max(open, close) ||
      candles.some((candle) => candle.date === date)
    )
      return { candles: [], annotations: [], error: "Saved candles are incomplete or invalid." };
    candles.push({ evidenceId: item.id, date, open, high, low, close, volume });
  }
  candles.sort((a, b) => a.date.localeCompare(b.date));
  const annotations = report.decision?.chartAnnotations ?? [];
  const dates = new Set(candles.map((candle) => candle.date));
  const invalid = annotations.some((annotation) => {
    const cited = annotation.evidenceIds.map((id) =>
      snapshot.evidence.find((item) => item.id === id),
    );
    const prices = cited.flatMap((item) =>
      item === undefined ||
      (annotation.kind === "event" && item.id !== `${report.symbol}:${annotation.startSessionDate}`)
        ? []
        : ["open", "high", "low", "close", "high60Day", "low60Day"].map((key) => item.values[key]),
    );
    return (
      !["level", "zone", "event"].includes(annotation.kind) ||
      !["bullish", "bearish", "neutral"].includes(annotation.tone) ||
      !dates.has(annotation.startSessionDate) ||
      !dates.has(annotation.endSessionDate) ||
      annotation.startSessionDate > annotation.endSessionDate ||
      annotation.lowerPrice > annotation.upperPrice ||
      (annotation.kind !== "zone" && annotation.lowerPrice !== annotation.upperPrice) ||
      (annotation.kind === "event" && annotation.startSessionDate !== annotation.endSessionDate) ||
      annotation.evidenceIds.length === 0 ||
      cited.some((item) => !item?.id.startsWith(`${report.symbol}:`)) ||
      ![annotation.lowerPrice, annotation.upperPrice].every(
        (price) => Number.isFinite(price) && price > 0 && prices.includes(price),
      )
    );
  });
  return {
    candles,
    annotations: invalid ? [] : annotations,
    error: candles.length === 0 ? "No daily candles were saved with this report." : undefined,
    annotationError: invalid
      ? "Saved annotations do not match the evidence and cannot be displayed."
      : undefined,
  };
}
